/**
 * Reuse before build: what an installed app or a template covers, how a request matches them, and what a new version
 * changes. Pure functions, so the Developer's judgement has a tested core.
 */
import { AppManifestSchema, type AppManifest } from './manifest';
import type { AppTemplate } from './templates/template';

/** What an app offers, in words: for listings, matching and the approval card. */
export interface ManifestSummary {
  key: string;
  name: string;
  description: string | undefined;
  types: string[];
  /** Built-in types the app redefines for the tenant. */
  overrides: string[];
  views: string[];
  nav: string[];
}

export function summarizeManifest(manifest: AppManifest): ManifestSummary {
  return {
    key: manifest.key,
    name: manifest.name,
    description: manifest.description,
    types: manifest.objectTypes.map((type) => type.displayName),
    overrides: manifest.objectTypes.filter((type) => !type.key.startsWith('app:')).map((type) => type.key),
    views: manifest.views.map((view) => `${view.key} (${view.kind} of ${view.objectType})`),
    nav: manifest.nav.items.map((item) => `${item.label} ${item.path}`),
  };
}

const STOP_WORDS = new Set(['a', 'an', 'the', 'and', 'or', 'of', 'for', 'to', 'in', 'on', 'with', 'by', 'our', 'we', 'i', 'my', 'me', 'us', 'need', 'want', 'please', 'app', 'new', 'build', 'make', 'create', 'track', 'tracking', 'tracker', 'something', 'that', 'this', 'it', 'is', 'are', 'be', 'can', 'so', 'from', 'into', 'at', 'as']);

/** Lower-case word stems: punctuation dropped, plurals and a few suffixes trimmed, stop words left out. */
export function requestTerms(text: string): string[] {
  const terms = new Set<string>();
  for (const raw of text.toLowerCase().split(/[^a-z0-9]+/)) {
    if (raw.length < 3 || STOP_WORDS.has(raw)) continue;
    terms.add(stem(raw));
  }
  return [...terms];
}

function stem(word: string): string {
  if (word.endsWith('ies') && word.length > 4) return `${word.slice(0, -3)}y`;
  if (word.endsWith('es') && word.length > 4 && /(s|x|z|ch|sh)es$/.test(word)) return word.slice(0, -2);
  if (word.endsWith('s') && !word.endsWith('ss') && word.length > 3) return word.slice(0, -1);
  return word;
}

export interface AppCandidate {
  kind: 'installed' | 'template';
  key: string;
  name: string;
  /** Words from the request that this app covers. */
  matched: string[];
  score: number;
}

export interface InstalledAppFacts {
  key: string;
  name: string;
  /** Display names of its types and its nav labels. */
  words: readonly string[];
}

function candidateTerms(words: readonly string[]): Set<string> {
  const terms = new Set<string>();
  for (const phrase of words) for (const term of requestTerms(phrase)) terms.add(term);
  return terms;
}

/**
 * Ranks installed apps and templates by how many of the request's words they cover; nothing with no overlap is listed.
 * Installed apps rank above templates at equal score, since extending what exists beats installing a second app.
 */
export function matchAppRequest(request: string, installed: readonly InstalledAppFacts[], templates: readonly AppTemplate[]): AppCandidate[] {
  const terms = requestTerms(request);
  if (terms.length === 0) return [];
  const candidates: AppCandidate[] = [];
  const consider = (kind: AppCandidate['kind'], key: string, name: string, words: readonly string[]) => {
    const covered = candidateTerms(words);
    const matched = terms.filter((term) => covered.has(term));
    if (matched.length > 0) candidates.push({ kind, key, name, matched, score: matched.length });
  };
  // Descriptions are prose and match too easily ("dates", "status"); keys, names, tags, types and nav labels are the signal.
  for (const app of installed) consider('installed', app.key, app.name, [app.key, app.name, ...app.words]);
  for (const template of templates) {
    const parsed = AppManifestSchema.safeParse(template.manifest);
    const summary = parsed.success ? summarizeManifest(parsed.data) : null;
    consider('template', template.key, template.name, [template.key, template.name, ...template.tags, ...(summary?.types ?? []), ...(summary?.nav ?? [])]);
  }
  return candidates.sort((a, b) => b.score - a.score || (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'installed' ? -1 : 1));
}

export interface ManifestDiff {
  typesAdded: string[];
  typesRemoved: string[];
  /** Per type kept, the fields added and removed. */
  fieldsAdded: Record<string, string[]>;
  fieldsRemoved: Record<string, string[]>;
  edgeTypesAdded: string[];
  edgeTypesRemoved: string[];
  viewsAdded: string[];
  viewsRemoved: string[];
  navAdded: string[];
  navRemoved: string[];
  picklistsAdded: string[];
  picklistsRemoved: string[];
  personasAdded: string[];
  personasRemoved: string[];
  /** Personas in both versions whose definition differs. */
  personasChanged: string[];
}

function difference(a: readonly string[], b: readonly string[]): string[] {
  const other = new Set(b);
  return a.filter((item) => !other.has(item));
}

/** What `next` adds and removes compared with `previous`, by key; renamed labels do not count. */
export function diffManifests(previous: AppManifest, next: AppManifest): ManifestDiff {
  const previousTypes = new Map(previous.objectTypes.map((type) => [type.key, type]));
  const nextTypes = new Map(next.objectTypes.map((type) => [type.key, type]));
  const fieldsAdded: Record<string, string[]> = {};
  const fieldsRemoved: Record<string, string[]> = {};
  for (const [key, type] of nextTypes) {
    const before = previousTypes.get(key);
    if (!before) continue;
    const added = difference(type.fields.map((field) => field.key), before.fields.map((field) => field.key));
    const removed = difference(before.fields.map((field) => field.key), type.fields.map((field) => field.key));
    if (added.length > 0) fieldsAdded[key] = added;
    if (removed.length > 0) fieldsRemoved[key] = removed;
  }
  const navKeys = (manifest: AppManifest) => manifest.nav.items.map((item) => item.path);
  return {
    typesAdded: difference([...nextTypes.keys()], [...previousTypes.keys()]),
    typesRemoved: difference([...previousTypes.keys()], [...nextTypes.keys()]),
    fieldsAdded,
    fieldsRemoved,
    edgeTypesAdded: difference(next.edgeTypes.map((edge) => edge.key), previous.edgeTypes.map((edge) => edge.key)),
    edgeTypesRemoved: difference(previous.edgeTypes.map((edge) => edge.key), next.edgeTypes.map((edge) => edge.key)),
    viewsAdded: difference(next.views.map((view) => view.key), previous.views.map((view) => view.key)),
    viewsRemoved: difference(previous.views.map((view) => view.key), next.views.map((view) => view.key)),
    navAdded: difference(navKeys(next), navKeys(previous)),
    navRemoved: difference(navKeys(previous), navKeys(next)),
    picklistsAdded: difference(next.picklists.map((picklist) => picklist.name), previous.picklists.map((picklist) => picklist.name)),
    picklistsRemoved: difference(previous.picklists.map((picklist) => picklist.name), next.picklists.map((picklist) => picklist.name)),
    personasAdded: difference(next.personas.map((persona) => persona.slug), previous.personas.map((persona) => persona.slug)),
    personasRemoved: difference(previous.personas.map((persona) => persona.slug), next.personas.map((persona) => persona.slug)),
    personasChanged: next.personas
      .filter((persona) => {
        const before = previous.personas.find((candidate) => candidate.slug === persona.slug);
        return before !== undefined && JSON.stringify(before) !== JSON.stringify(persona);
      })
      .map((persona) => persona.slug),
  };
}

/**
 * What `template` now ships that an installed app built from it lacks, as lines; empty when the app has everything.
 * Only additions count: what the app added on top of the template (its own types, fields or views) is not a gap.
 */
export function templateAdditions(installed: AppManifest, template: AppManifest): string[] {
  const diff = diffManifests(installed, template);
  return describeDiff({
    ...diff,
    typesRemoved: [],
    fieldsRemoved: {},
    edgeTypesRemoved: [],
    viewsRemoved: [],
    navRemoved: [],
    picklistsRemoved: [],
    personasRemoved: [],
    personasChanged: [],
  });
}

/** The diff as lines for an approval card; empty when nothing changed by key. */
export function describeDiff(diff: ManifestDiff): string[] {
  const lines: string[] = [];
  const list = (label: string, items: readonly string[]) => {
    if (items.length > 0) lines.push(`${label}: ${items.join(', ')}`);
  };
  list('Types added', diff.typesAdded);
  list('Types removed', diff.typesRemoved);
  for (const [type, fields] of Object.entries(diff.fieldsAdded)) list(`Fields added to ${type}`, fields);
  for (const [type, fields] of Object.entries(diff.fieldsRemoved)) list(`Fields removed from ${type}`, fields);
  list('Relationships added', diff.edgeTypesAdded);
  list('Relationships removed', diff.edgeTypesRemoved);
  list('Views added', diff.viewsAdded);
  list('Views removed', diff.viewsRemoved);
  list('Navigation added', diff.navAdded);
  list('Navigation removed', diff.navRemoved);
  list('Picklists added', diff.picklistsAdded);
  list('Picklists removed', diff.picklistsRemoved);
  list('Personas added', diff.personasAdded);
  list('Personas removed', diff.personasRemoved);
  list('Personas changed', diff.personasChanged);
  return lines;
}
